import { useMobileMediaQuery } from "app/shared/util/media-query";
import React, { useImperativeHandle, useState } from "react";
import { Pressable, ScrollView, StatusBar, StyleSheet, Text, useWindowDimensions, View } from "react-native";
import Modal from "react-native-modal";
import { Avatar, Dialog, Paragraph, Surface, useTheme } from "react-native-paper";
import ContentChangePassword from "./content-change-pass";
import { Block } from "app/shared/components/blocks/block";
import { spacing } from "app/shared/styles/spacing";
import { StackSpacing } from "app/shared/components/spacing";
import { useDispatch, useSelector } from "react-redux";
import { getUserProfileSelector } from "app/modules/account-management/reducer/selectors";
import { getTwoCharacterFromName } from "app/shared/util/validate";
import OTPChangePassword from "./otp";
import { useTranslation } from "react-i18next";
import { FormChangePasswordData } from "./reducer/types";
import { getUserProfileKeyCloakSelector } from "app/modules/login/reducer/selectors";
import { getChangePasswordSelector } from "./reducer/selectors";
import {
  fetchChangePasswordAuthRequest,
  fetchChangePasswordRequest,
  resetStoreChangePassword,
  setStoreChangePassword,
} from "./reducer/actions";
import auth from "app/shared/services/auth";
import { actionLogout } from "app/modules/login/reducer/actions";

export const createGlobalModalChangePassword = () => {
  const ref = React.createRef<any>();
  return ref;
};

export const globalModalChangePassword = {
  show: (globalModalChangePasswordRef: React.RefObject<any>) => {
    globalModalChangePasswordRef?.current?.show();
  },
  hide: (globalModalChangePasswordRef: React.RefObject<any>) => {
    globalModalChangePasswordRef?.current?.hide();
  },
};

const ChangePassword = React.forwardRef((props, ref) => {
  const [visible, setVisible] = useState<boolean>(false);
  const { width, height } = useWindowDimensions();
  const isMobile = useMobileMediaQuery();
  const { colors, fonts } = useTheme();
  const { t } = useTranslation();
  const dispatchStore = useDispatch();
  const accentColorStyle = { color: colors.accent };
  const buttonStyle = { ...fonts.medium };

  const userProfile = useSelector(getUserProfileSelector);
  const userProfileKeyCloak = useSelector(getUserProfileKeyCloakSelector);
  const changePassStore = useSelector(getChangePasswordSelector);

  const [stepIndex, setStepIndex] = React.useState(0);
  const [isPVConnect, setIsPVConnect] = React.useState(false);
  const [isForceChange, setIsForceChange] = React.useState(false);
  const [noteChange, setNoteChange] = useState<string>(t("SETTING_NOTE_DOI_MAT_KHAU_FORCE_CHANGE"));

  const styles = React.useMemo(
    () =>
      StyleSheet.create({
        modalContainerStyle: {
          alignSelf: "center",
          height: "auto",
          // minHeight: 200,
          borderRadius: 12,
        },
        scrollView: {
          maxHeight: 0.95 * height,
          display: stepIndex === 2 ? "none" : "flex",
        },
        nameStyles: { ...fonts.semibold, color: colors.text, fontSize: 12, maxWidth: "100%", textAlign: "center" },
        container: {
          display: stepIndex === 2 ? "flex" : "none",
        },
        titleStyle: {
          letterSpacing: 0,
          textAlign: "center",
        },
        textStyle: {
          textAlign: "center",
        },
        messageStyle: {
          color: "black",
          letterSpacing: 0,
          textAlign: "center",
        },
        actionStyle: {
          justifyContent: "center",
        },
      }),
    [height, stepIndex],
  );

  const styleBackdrop: any = {
    position: "fixed",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: "#000",
  };

  const customBackdrop = <View style={styleBackdrop} />;

  useImperativeHandle(ref, () => {
    return { show: show, hide: hide };
  });

  const show = (_content: JSX.Element) => {
    setVisible(true);
  };

  const hide = () => {
    setVisible(false);
  };

  const cancelChange = () => {
    dispatchStore(resetStoreChangePassword());
    if (isForceChange) {
      onClickLogOut();
    }
    hide();
  };

  const requestChangePassword = (data: FormChangePasswordData) => {
    console.log("requestChangePassword: ", data);
    data.isPVConnect = isPVConnect;
    dispatchStore(fetchChangePasswordRequest(data));
  };

  const cancelOTP = () => {
    setStepIndex(0);
    dispatchStore(
      setStoreChangePassword({
        ...changePassStore,
        statusCreate: "init",
        errorMsg: "",
        responseCreate: undefined,
        errorOTP: false,
        statusAuth: "init",
      }),
    );
  };

  const requestAuthOTP = (data: FormChangePasswordData) => {
    console.log("requestAuthOTP: ", data);
    dispatchStore(fetchChangePasswordAuthRequest(changePassStore.responseCreate, data.otpCode));
  };

  const onClickLogOut = () => {
    auth.logout();
    dispatchStore(actionLogout());
  };

  React.useEffect(() => {
    // Check is IB MB/PVConnect
    if (userProfileKeyCloak) {
      console.log("userProfileKeyCloak: ", userProfileKeyCloak);
      let isPVconnect = true;
      if (userProfileKeyCloak.attributes?.federationLink && userProfileKeyCloak.attributes.federationLink[0] === "ebank") {
        isPVconnect = false;
      }
      console.log("isPVconnect: ", isPVconnect);
      setIsPVConnect(isPVconnect);
    }
  }, [userProfileKeyCloak]);

  React.useEffect(() => {
    // Check Force change pass
    if (userProfile) {
      console.log("userProfile: ", userProfile);
      let isForceChange = false;
      if (userProfile.PASSWORDCHANGED === "N" || userProfile.PASSWORDCHANGED === "X" || userProfile.PASSWORDCHANGED === "S") {
        if (userProfile.PASSWORDCHANGED === "N") setNoteChange(t("SETTING_NOTE_DOI_MAT_KHAU_LAN_DAU"));
        if (userProfile.PASSWORDCHANGED === "S") setNoteChange(t("SETTING_NOTE_DOI_MAT_KHAU_FORCE_CHANGE"));
        if (userProfile.PASSWORDCHANGED === "X") setNoteChange(t("SETTING_NOTE_DOI_MAT_KHAU_HET_HAN"));
        isForceChange = true;
      }
      setIsForceChange(isForceChange);
    }
  }, [userProfile]);

  React.useEffect(() => {
    if (changePassStore.statusCreate === "ok") {
      setStepIndex(1);
    }
  }, [changePassStore.statusCreate]);

  React.useEffect(() => {
    if (changePassStore.statusAuth === "ok") {
      setStepIndex(2);
    }
  }, [changePassStore.statusAuth]);

  const FormAccountInfo = (
    <Block flex={1} p={spacing.small} br={12}>
      <Block middle row center>
        <Pressable>
          <Avatar.Text size={54} label={getTwoCharacterFromName(userProfile?.FULLNAME)} />
        </Pressable>
      </Block>
      <StackSpacing size="small" />
      <Block flex={1} flexwrap>
        <Text numberOfLines={2} style={styles.nameStyles}>
          {userProfile?.FULLNAME || t("COMMON_HO_TEN")}
        </Text>
      </Block>
    </Block>
  );

  return (
    <Modal
      deviceHeight={height}
      deviceWidth={width}
      onBackdropPress={() => {}}
      animationIn="slideInUp"
      customBackdrop={customBackdrop}
      animationOut="slideOutUp"
      hideModalContentWhileAnimating
      isVisible={visible}>
      <StatusBar translucent backgroundColor={"rgba(0,0,0,0.6)"} barStyle={"light-content"} />
      <Surface style={[styles.modalContainerStyle, isMobile ? { width: width * 0.9 } : { width: 380 }]}>
        <ScrollView style={styles.scrollView}>
          <Block flex={1} br={12}>
            {FormAccountInfo}
            <Block display={stepIndex === 0 ? "flex" : "none"}>
              <ContentChangePassword
                isForceChange={isForceChange}
                changePassStore={changePassStore}
                closeModal={cancelChange}
                requestChange={requestChangePassword}
                noteChange={noteChange}
              />
            </Block>
            {stepIndex === 1 ? (
              <OTPChangePassword
                challengeValue={changePassStore.responseCreate?.CHALLENGE_VALUE}
                cancel={cancelOTP}
                request={requestAuthOTP}
                errorEndOTP={changePassStore.errorOTP}
                changePassStore={changePassStore}
              />
            ) : (
              <></>
            )}
          </Block>
        </ScrollView>
        <View style={styles.container}>
          <Dialog.Title children={t("COMMON_THONG_BAO")} style={styles.titleStyle} />
          <Dialog.Content children={<Paragraph style={styles.messageStyle} children={t("SETTING_THAY_DOI_MAT_KHAU_THANH_CONG")} />} />
          <Dialog.Actions style={styles.actionStyle}>
            <Pressable onPress={onClickLogOut}>
              <Paragraph style={[accentColorStyle, buttonStyle]} children={t("COMMON_TIEP_TUC")} />
            </Pressable>
          </Dialog.Actions>
        </View>
      </Surface>
    </Modal>
  );
});

export default ChangePassword;
